import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  MessageSquare,
  ClipboardCheck,
  LayoutDashboard
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { getProposalStatusCounts, getUserProposals, getPendingReviewProposals } from '../services/proposalService';
import DashboardPipelineCards from './DashboardPipelineCards';
import DashboardRecentProposals from './DashboardRecentProposals';
import DashboardAnalyticsSection from './DashboardAnalyticsSection';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { canReviewProposals, canReadAnalytics } = usePermissions();
  const { tenant } = useTenant();
  const workflowMode = tenant?.settings?.workflowMode || 'hybrid';

  const [statusCounts, setStatusCounts] = useState({});
  const [recentProposals, setRecentProposals] = useState([]);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [countsRes, proposalsRes] = await Promise.all([
          getProposalStatusCounts(),
          getUserProposals({ page: 1, limit: 5 })
        ]);

        setStatusCounts(countsRes?.counts || {});
        setRecentProposals(proposalsRes?.data || []);

        // Non-fatal: fetch pending review count if user has permission
        if (canReviewProposals()) {
          try {
            const reviewRes = await getPendingReviewProposals({ limit: 1 });
            setPendingReviewCount(reviewRes?.pagination?.total || 0);
          } catch {
            // Silently fail — not critical
          }
        }
      } catch (err) {
        console.error('Dashboard data fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Welcome */}
      <div className="flex items-center gap-3">
        <div className="bg-primary-100 p-2 rounded-lg">
          <LayoutDashboard className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Welcome back, {firstName}</h1>
          <p className="text-sm text-gray-500">Here's your proposal pipeline overview.</p>
        </div>
      </div>

      {/* Pipeline Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Proposal Pipeline</h2>
        <DashboardPipelineCards counts={statusCounts} loading={loading} workflowMode={workflowMode} />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-left"
        >
          <Search className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-gray-700">Find Work</span>
        </button>
        <button
          onClick={() => navigate('/proposals')}
          className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-left"
        >
          <FileText className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-gray-700">View Proposals</span>
        </button>
        <button
          onClick={() => navigate('/messages')}
          className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-left"
        >
          <MessageSquare className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-gray-700">Messages</span>
        </button>
        {canReviewProposals() && (
          <button
            onClick={() => navigate('/proposals?status=under_review')}
            className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-left relative"
          >
            <ClipboardCheck className="h-4 w-4 text-primary-600" />
            <span className="text-sm font-medium text-gray-700">Review</span>
            {pendingReviewCount > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[1.25rem] flex items-center justify-center px-1">
                {pendingReviewCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Recent Proposals */}
      <DashboardRecentProposals proposals={recentProposals} loading={loading} />

      {/* Analytics Section — conditional on permission */}
      {canReadAnalytics() && (
        <DashboardAnalyticsSection />
      )}
    </div>
  );
};

export default Dashboard;
